import React, {useMemo} from 'react';

import {useStore} from 'effector-react';

import {Controller, DeepMap, FieldError, FieldValues, useWatch} from 'react-hook-form';
import {TFunction} from 'react-i18next';

import {
    $initalLineValuesHolder,
    $invoiceLinesHolder,
    copyInvoiceLineData,
    removeInvoiceLinesHolder,
} from '../../models/create';
import {setModalDuplicateLinesModal} from '../../models/details';
import {debounce} from '../../utils/debounce';

import {CustomHsnCodes} from '../FormsData/selectOptionsComponents/CustomHsnCodes';
import {isStandartCategory, VAT_CATEGORY} from '../helpers/consts';
import {LineControlComponent} from '../Pages/InvoiceLines/components/LineControl';
import {ModalHandler} from '../Pages/InvoiceLines/ModalHandler';
import {CustomRadioGroup} from '../SimpleRadio';
import {Input} from '../ui-kit/Input';
import {AsyncSelect, Select} from '../ui-kit/Select';
import {Tooltip} from '../ui-kit/Tooltip';

import {useFormLineCalculations} from './useFormLineCalculations';
import {useHsnFetching} from './useHsnFetching';

const CategoryCode = ({
    control,
    setValue,
    isMarkedItem,
}: {
    control: any;
    setValue: (x: string, q: string) => any;
    isMarkedItem: boolean;
}) => {
    const selectorName = 'serviceCategory';
    const isHsnName = 'isHsn';

    const isHsn =
        useWatch({
            control,
            name: isHsnName,
            defaultValue: 'HS',
        }) === 'HS';

    const loadOptions = useHsnFetching(isHsn);

    return (
        <div style={{width: '100%', display: 'flex', flexDirection: 'column', gap: '6px'}}>
            <Controller
                control={control}
                name={isHsnName}
                defaultValue={'HS'}
                render={(props) => {
                    const {ref, value, ...rest} = props;
                    return (
                        <CustomRadioGroup
                            disabled={isMarkedItem}
                            {...rest}
                            value={isMarkedItem ? 'HS' : value}
                            options={[
                                {
                                    label: 'HS',
                                    value: 'HS',
                                },
                                {
                                    label: 'SAC',
                                    value: 'MP',
                                },
                            ]}
                            onChange={(q) => {
                                props.onChange(q);
                                // setCategoryCode(q === 'HS');
                                setValue(selectorName, '');
                            }}
                        />
                    );
                }}
            />
            <Controller
                control={control}
                name={selectorName}
                defaultValue={''}
                render={(props) => (
                    <AsyncSelect
                        size="sm"
                        {...props}
                        isSearchable={true}
                        defaultOptions={[]}
                        disabled={isMarkedItem}
                        menuPlacement={'bottom'}
                        menuPosition={'fixed'}
                        optionComponent={CustomHsnCodes}
                        loadOptions={loadOptions}
                        placeholder={isHsn ? 'MP - Product' : 'HS - code'}
                    />
                )}
            />
        </div>
    );
};

export const useCombinedData2 = ({
    control,
    t,
    onEnterCode,
    getValues,
    setValue,
    trigger,
    errors,
}: {
    control: any;
    t: TFunction<'translation'>;
    onEnterCode: (code: string, fromLine?: boolean) => Promise<void>;
    getValues: (x?: string | string[]) => any;
    setValue: (name: any, value: any) => any;
    trigger: (name?: any) => Promise<boolean>;
    errors: DeepMap<FieldValues, FieldError>;
}) => {
    const invoiceLinesHolder = useStore($invoiceLinesHolder);
    const initalLineValuesHolder = useStore($initalLineValuesHolder);
    const GEN_INFO = 'pages.documents.invoices.create.invoiceLines';
    const vatCategory = useWatch<{value: string; label: string}>({
        control,
        name: 'vatCategory',
        defaultValue: {value: '', label: ''},
    });

    const {combineValues, calculateLineAmountWVat} = useFormLineCalculations({
        getValues,
        setValue,
        trigger,
    });

    const debouncedOnEnterCode = useMemo(
        () => debounce((code: string, fromLine: boolean) => onEnterCode(code, fromLine), 230),
        [onEnterCode],
    );

    const additionalStyles = `
        text-align: left;
        max-width: 250px;
        border-radius: 8px;
        opacity: 1 !important;
    `;

    const index = -1;

    function isFieldFilled(field: string, fieldValue: any): boolean {
        if (fieldValue === undefined || fieldValue === null || fieldValue === '') {
            return false;
        }

        if (field === 'vatCategory' || field === 'serviceCategory') {
            const objValue = fieldValue as {value?: string} | undefined;
            return Boolean(objValue?.value && objValue.value !== '');
        }

        const stringValue = String(fieldValue);
        return stringValue !== '';
    }

    const description = useWatch({control, name: 'description', defaultValue: ''});
    const qty = useWatch({control, name: 'qty', defaultValue: ''});
    const price = useWatch({control, name: 'price', defaultValue: ''});
    const vat = useWatch({control, name: 'vat', defaultValue: ''});

    const currentFormValues = useMemo(
        () => ({
            description,
            qty,
            price,
            vat,
            vatCategory,
        }),
        [description, qty, price, vat, vatCategory],
    );

    const requiredFields = ['qty', 'price', 'vatCategory', 'vat'] as const;

    const areRequiredFieldsFilled = useMemo(
        () =>
            requiredFields.every((field) => {
                const fieldValue = currentFormValues[field];
                return isFieldFilled(field, fieldValue);
            }),
        [currentFormValues],
    );

    const allowToCopy = useMemo(
        () =>
            !!initalLineValuesHolder &&
            initalLineValuesHolder.isMarkedItem === true &&
            areRequiredFieldsFilled,
        [initalLineValuesHolder, areRequiredFieldsFilled],
    );

    const allowToDuplicateDown = useMemo(
        () =>
            !!initalLineValuesHolder &&
            initalLineValuesHolder.isMarkedItem === false &&
            areRequiredFieldsFilled,
        [initalLineValuesHolder, areRequiredFieldsFilled],
    );

    const controlFields = useMemo(
        () => ({
            lineId: '',
            control: (
                <LineControlComponent
                    fieldsLength={invoiceLinesHolder.length}
                    onCopyData={() => {
                        copyInvoiceLineData({
                            index: index.toString(),
                            initialLineData: {
                                ...currentFormValues,
                                serviceCategory: {
                                    label: getValues('serviceCategory').label,
                                    value: getValues('serviceCategory').value,
                                },
                                lines: getValues('lines'),
                                lineAmountWVat: getValues('lineAmountWVat'),
                            },
                        });
                    }}
                    onDuplicateDown={() => {
                        setModalDuplicateLinesModal({
                            index: -1,
                            show: true,
                            initialLineData: {
                                ...currentFormValues,
                                serviceCategory: {
                                    label: getValues('serviceCategory').label,
                                    value: getValues('serviceCategory').value,
                                },
                                lines: getValues('lines'),
                                lineAmountWVat: getValues('lineAmountWVat'),
                            },
                        });
                    }}
                    onRemoveField={() => removeInvoiceLinesHolder(index.toString())}
                    allowToCopyData={allowToCopy ?? false}
                    allowToDuplicateDown={allowToDuplicateDown ?? false}
                    allowToDelete={false}
                />
            ),
            charges: <ModalHandler getValues={getValues} setValue={setValue} trigger={trigger} />,
            // codeGtin: (
            //     <>
            //         <Tooltip
            //             id={'codeGtin'}
            //             place={'top'}
            //             backgroundColor="white"
            //             textColor="#111111"
            //             additionalStyles={
            //                 additionalStyles + 'box-shadow: 0px 3px 6px rgba(116, 116, 116, 0.3);'
            //             }
            //         />
            //         <Controller
            //             control={control}
            //             name={'codeGtin'}
            //             defaultValue={''}
            //             render={(props) => (
            //                 <Input
            //                     data-tip={t(`${GEN_INFO}.codeGtinTooltip`)}
            //                     data-for={'codeGtin'}
            //                     size="sm"
            //                     {...props}
            //                     placeholder={t(`${GEN_INFO}.codeGtin`)}
            //                     onChange={(e) => {
            //                         // const trimmedVal = e.target.value.trim().replace(/\s/g, '');
            //                         // debouncedOnEnterCode(trimmedVal, true);
            //                         props.onChange(e);
            //                     }}
            //                 />
            //             )}
            //         />
            //     </>
            // ),
            description: (
                <Controller
                    control={control}
                    name={'description'}
                    defaultValue={''}
                    render={(props) => (
                        <Input
                            size="sm"
                            {...props}
                            disabled={initalLineValuesHolder?.isMarkedItem ?? false}
                            placeholder={t(`${GEN_INFO}.description`)}
                            onChange={(v) => {
                                props.onChange(v);
                            }}
                            hasError={errors['description']}
                        />
                    )}
                />
            ),
            itemCode: (
                <Controller
                    control={control}
                    name={'itemCode'}
                    defaultValue={''}
                    render={(props) => (
                        <Input
                            size="sm"
                            {...props}
                            disabled={initalLineValuesHolder?.isMarkedItem ?? false}
                            placeholder={t(`${GEN_INFO}.itemCode`)}
                            onChange={(v) => {
                                props.onChange(v);
                            }}
                            hasError={errors['itemCode']}
                        />
                    )}
                />
            ),
            qty: (
                <Controller
                    control={control}
                    name={'qty'}
                    defaultValue={''}
                    render={(props) => (
                        <Input
                            size="sm"
                            {...props}
                            type="number"
                            min={1}
                            disabled={initalLineValuesHolder?.isMarkedItem ?? false}
                            placeholder={t(`${GEN_INFO}.qty`)}
                            onChange={(q) => {
                                props.onChange(q);
                                combineValues();
                            }}
                            hasError={errors['qty']}
                        />
                    )}
                />
            ),
            price: (
                <Controller
                    control={control}
                    name={'price'}
                    defaultValue={''}
                    render={(props) => (
                        <Input
                            size="sm"
                            {...props}
                            type="number"
                            step={'0.01'}
                            placeholder={t(`${GEN_INFO}.pricePlaceholder`)}
                            onChange={(q) => {
                                props.onChange(q);
                                combineValues();
                                calculateLineAmountWVat();
                            }}
                            hasError={errors['price']}
                        />
                    )}
                />
            ),
            serviceCategory: (
                <CategoryCode
                    control={control}
                    setValue={setValue}
                    isMarkedItem={initalLineValuesHolder?.isMarkedItem ?? false}
                />
            ),
            vatCategory: (
                <Controller
                    control={control}
                    name={'vatCategory'}
                    defaultValue={''}
                    render={(props) => (
                        <Select
                            size="sm"
                            {...props}
                            options={VAT_CATEGORY}
                            onChange={async (q: any) => {
                                props.onChange(q);
                                setValue('vat', '0');
                                calculateLineAmountWVat();
                                trigger('vat');
                            }}
                            menuPosition={'fixed'}
                            hasError={errors['vatCategory']}
                        />
                    )}
                />
            ),
            vat: (
                <Controller
                    control={control}
                    name={'vat'}
                    defaultValue={''}
                    render={(props) => (
                        <Input
                            size="sm"
                            {...props}
                            placeholder={'VAT'}
                            type="number"
                            step={'0.1'}
                            min={!isStandartCategory(vatCategory?.value) ? '0' : '0.1'}
                            disabled={!isStandartCategory(vatCategory?.value)}
                            onChange={(q) => {
                                props.onChange(q);
                                calculateLineAmountWVat();
                            }}
                            hasError={errors['vat']}
                        />
                    )}
                />
            ),
            lines: (
                <Controller
                    control={control}
                    name={'lines'}
                    defaultValue={''}
                    render={(props) => <Input disabled size="sm" {...props} placeholder={'Auto'} />}
                />
            ),
            lineAmountWVat: (
                <Controller
                    control={control}
                    name={'lineAmountWVat'}
                    defaultValue={''}
                    render={(props) => <Input disabled size="sm" {...props} placeholder={'Auto'} />}
                />
            ),
        }),
        [
            initalLineValuesHolder,
            allowToCopy,
            allowToDuplicateDown,
            invoiceLinesHolder.length,
            t,
            GEN_INFO,
            additionalStyles,
            control,
            setValue,
            getValues,
            trigger,
            debouncedOnEnterCode,
            combineValues,
            calculateLineAmountWVat,
            vatCategory,
        ],
    );

    return {
        mappedFields2: [controlFields],
    };
};
